import { Tally } from '@stratumn/atomic';
import graphql from 'babel-plugin-relay/macro';
import {
  ROUTE_BOTS,
  ROUTE_COLLABORATORS,
  ROUTE_MEMBERS,
  ROUTE_ORG_SETTINGS,
  ROUTE_TEAMS
} from 'constant/routes';
import compose from 'lodash.flowright';
import PropType from 'prop-types';
import injectSheet from 'react-jss';
import { createFragmentContainer } from 'react-relay';
import { generatePath, NavLink, withRouter } from 'react-router-dom';
import { hasElevatedRights } from 'utils/elevatedRights';
import { withGqlClient, withUser } from 'wrappers';

import { ChangeOrganization } from '../changeOrganization';
import styles from './organization.style';

export function Organization({ classes, organization, theme, user }) {
  const {
    account: { members },
    collaborators,
    teams,
    rowId,
    bots
  } = organization;

  /**
   * NavLink activeClassName from react-router-dom overides className from react-jss.
   * Therefore, we have to use inline style to make sure our color is appropriately adjusted when the sidenav link is active.
   */
  const sidenavLinkStyle = {
    fontWeight: 700,
    color: theme.grey1
  };

  return (
    <>
      <div className={classes.orgTitle}>
        <ChangeOrganization />
      </div>
      <nav className={classes.subHeader}>
        <NavLink
          activeStyle={sidenavLinkStyle}
          className={classes.sidenavLink}
          draggable="false"
          strict
          to={`${generatePath(ROUTE_MEMBERS, { orgId: rowId })}`}
        >
          <p>Members</p>
          <span className={classes.tally}>
            <Tally value={members.totalCount} />
          </span>
        </NavLink>
        <NavLink
          activeStyle={sidenavLinkStyle}
          className={classes.sidenavLink}
          draggable="false"
          strict
          to={`${generatePath(ROUTE_BOTS, { orgId: rowId })}`}
        >
          <p>Bots</p>
          <span className={classes.tally}>
            <Tally value={bots.totalCount} />
          </span>
        </NavLink>
        <NavLink
          activeStyle={sidenavLinkStyle}
          className={classes.sidenavLink}
          draggable="false"
          strict
          to={`${generatePath(ROUTE_TEAMS, { orgId: rowId })}`}
        >
          <p>Teams</p>
          <span className={classes.tally}>
            <Tally value={teams.totalCount} />
          </span>
        </NavLink>
        <NavLink
          activeStyle={sidenavLinkStyle}
          className={classes.sidenavLink}
          draggable="false"
          strict
          to={generatePath(ROUTE_COLLABORATORS, { orgId: rowId })}
        >
          <p>Collaborators</p>
          <span className={classes.tally}>
            <Tally value={collaborators.totalCount} />
          </span>
        </NavLink>
        {hasElevatedRights(user) && (
          <NavLink
            activeStyle={sidenavLinkStyle}
            className={classes.sidenavLink}
            draggable="false"
            strict
            to={generatePath(ROUTE_ORG_SETTINGS, { orgId: rowId })}
          >
            <p>Settings</p>
          </NavLink>
        )}
      </nav>
    </>
  );
}

Organization.propTypes = {
  classes: PropType.object.isRequired,
  organization: PropType.object.isRequired,
  theme: PropType.object.isRequired,
  user: PropType.object
};

Organization.defaultProps = {
  user: null
};

export default createFragmentContainer(
  compose(
    withGqlClient,
    withRouter,
    withUser,
    injectSheet(styles)
  )(Organization),
  {
    organization: graphql`
      fragment organization_organization on Organization {
        rowId
        name
        avatar
        canUpdate
        isLeader
        account {
          members {
            totalCount
          }
        }
        collaborators {
          totalCount
        }
        teams {
          totalCount
        }
        bots {
          totalCount
        }
      }
    `
  }
);
